import React, { FC, ReactElement, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from 'styled-components';
// eslint-disable-next-line no-restricted-imports
import {
    selectIsFromProducts,
    selectIsFromSearchApp,
    selectIsFromYaRu,
} from '../../data-layer/meta/selectors';
// eslint-disable-next-line no-restricted-imports
import { useSelector } from '../../data-layer/useSelector';
import { getIsMobile } from '../../utils/getIsMobile';
import { getWindow } from '../../utils/getWindow';
import { ymTrackClickToInstall } from '../../utils/metrika';
import { useBackButton } from '../../utils/useBackButton';
import { useTranslation } from '../../utils/useTranslation';
import { APP_DOWNLOAD_LINK } from '../discovery/AppDiscoveryMobile';
import { IconArrowLeft } from '../icon/IconArrowLeft';
import { Button } from '../input/Button';
import { Flex } from '../layout/Flex';
import { MenuItem } from '../navigation/Menu';
import { Typography } from '../typography/Typography';
import { Logo, LogoTransparent } from './Logo';
import { isPathInBlacklist } from './utils/isPathInBlacklist';

export const HEADER_HEIGHT = 52;

interface ContainerProps {
    transparent?: boolean;
}

const Container = styled(Flex)
    .withConfig({ displayName: 'Container' })
    .attrs<ContainerProps>((props) => ({
        ...props,
        width: '100%',
        height: HEADER_HEIGHT,
        direction: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backgroundColor: props.transparent ? undefined : 'var(--color-invert-blur)',
        backdropFilter: props.transparent ? undefined : 'blur(25px)',
        pt: props.transparent ? 3 : undefined,
    }))`
    position: fixed;
`;

interface ArrowContainerParams {
    enabled: boolean;
    transparent?: boolean;
}

const ArrowContainer = styled(Flex)
    .withConfig({ displayName: 'ArrowContainer' })
    .attrs<ArrowContainerParams>((props) => ({
        color: 'var(--color-primary)',
        justifyContent: 'center',
        alignItems: 'center',
        height: props.transparent ? 40 : 52,
        width: props.transparent ? 40 : 54,
        marginLeft: { sm: 2 },
        left: props.transparent ? 1 : 0,
        top: props.transparent ? 2 : 0,
        transition: 'opacity 0.2s',
        opacity: props.enabled ? 1 : 0,
        borderRadius: '20px',
        backgroundColor: props.transparent ? 'var(--color-invert-blur)' : undefined,
        backdropFilter: props.transparent ? 'blur(50px)' : undefined,
    }))`
    position: absolute;
`;

interface ContextMenuContaierParams {
    transparent?: boolean;
}

const ContextMenuContaier = styled(Flex)
    .withConfig({ displayName: 'ContextMenuContaier' })
    .attrs<ContextMenuContaierParams>((props) => ({
        color: 'var(--color-primary)',
        justifyContent: 'center',
        alignItems: 'center',
        height: props.transparent ? 40 : 52,
        width: props.transparent ? 40 : 54,
        right: props.transparent ? 1 : 0,
        top: props.transparent ? 2 : 0,
        borderRadius: '20px',
        backgroundColor: props.transparent ? 'var(--color-invert-blur)' : undefined,
        backdropFilter: props.transparent ? 'blur(50px)' : undefined,
    }))`
    position: absolute;
`;

const AppButtonContainer = styled(Flex).withConfig({ displayName: 'AppButtonContainer' }).attrs({
    color: 'var(--color-primary)',
    justifyContent: 'center',
    alignItems: 'center',
    height: 52,
    top: 0,
    right: 1,
})`
    position: absolute;
`;

export interface DashboardHeaderProps {
    transparent?: boolean;
    menuSlot?: (params: { appMenuItem: ReactElement }) => ReactElement | null;
    backButtonEnabled?: boolean;
}

export const DashboardHeader: FC<DashboardHeaderProps> = ({
    backButtonEnabled: externalBackButtonEnabled = true,
    menuSlot,
    transparent,
}) => {
    const onNavigateBack = useBackButton();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { isMobile } = getIsMobile();
    const t = useTranslation();

    const isFromSearchApp = useSelector(selectIsFromSearchApp);
    const isFromProducts = useSelector(selectIsFromProducts);
    const isFromYaRu = useSelector(selectIsFromYaRu);
    const isFromExternalPage = isFromSearchApp || isFromProducts || isFromYaRu;

    const onDownloadClick = () => {
        ymTrackClickToInstall();
        getWindow()?.open(APP_DOWNLOAD_LINK, '_blank');
    };

    const menu = useMemo(
        () =>
            menuSlot
                ? menuSlot({
                      appMenuItem: (
                          <MenuItem onClick={onDownloadClick}>
                              <Typography>{t('Приложение')}</Typography>
                          </MenuItem>
                      ),
                  })
                : null,
        [menuSlot, t],
    );

    const onLogoClick = useCallback(() => {
        if (pathname !== '/') {
            navigate('/');
        }
    }, [navigate, pathname]);

    if (!isMobile) {
        return null;
    }

    const backButtonEnabled =
        externalBackButtonEnabled && isFromExternalPage !== undefined && !isFromExternalPage;
    const menuEnabled = menuSlot !== undefined;

    return (
        <Container transparent={transparent} left={{ sm: 0 }}>
            <ArrowContainer transparent={transparent} enabled={backButtonEnabled}>
                <IconArrowLeft
                    color="var(--color-primary)"
                    size={24}
                    onClick={backButtonEnabled ? onNavigateBack : undefined}
                />
            </ArrowContainer>
            {transparent ? (
                <LogoTransparent height={48} onClick={onLogoClick} />
            ) : (
                <Logo height={48} onClick={onLogoClick} />
            )}
            {menuEnabled ? (
                <ContextMenuContaier transparent={transparent}>{menu}</ContextMenuContaier>
            ) : null}
            {!menuEnabled && !isFromSearchApp && !isPathInBlacklist(pathname) && (
                <AppButtonContainer>
                    <Button size="m" color="secondary" height={40} onClick={onDownloadClick}>
                        {t('ПРИЛОЖЕНИЕ')}
                    </Button>
                </AppButtonContainer>
            )}
        </Container>
    );
};
